import {
  convert,
  newDate,
  score,
  extractKeys,
  strIndex,
  getFields
} from '@/sui/data/flatten/tools'

import {
  episodAge,
  episodDays,
  findSurveillance,
  initializeSurveillance,
  addBacteremia,
  addPneumopathy,
  addBMRBHreMO,
  ktDuration,
  unitType
} from '@/sui/data/flatten/common'

function addSepsis (prefix, target, parent, bact, v) {
  const config = [
    { key: 'episod', get: b => newDate(b.episod) },
    { key: 'date', get: b => newDate(b.diagDate) },
    { key: 'origin', get: b => b.origin },
    { key: 'entrance', get: b => b.entrance_v2 },
    { key: 'a13_kt_type', get: b => b.catheterType_v2 },
    { key: 'insertion', get: b => newDate(b.insertion) },
    { key: 'site', get: b => b.insertionSite },
    { key: 'removed', get: b => b.removed },
    { key: 'removal', get: b => newDate(b.removal) },
    { key: 'duration', get: b => ktDuration(parent, b) },
    { key: 'covid', get: b => b.covidStatus },
    { key: 'lipids', get: b => b.lipids },
    { key: 'urinary', get: b => b.urinary },
    { key: 'urinary_start', get: b => newDate(b.urinaryStart) },
    { key: 'urinary_removed', get: b => b.urinaryRemoved },
    { key: 'urinary_end', get: b => newDate(b.urinaryEnd) },
    { key: 'intubated', get: b => b.intubated },
    { key: 'intub_start', get: b => newDate(b.intubStart) }
  ]

  if (v >= 2) {
    config.push(
      { key: 'reintubated', get: b => b.reintubated },
      { key: 'intub_end', get: b => newDate(b.intubStart) }
    )
  }
  getFields(config, prefix, target, bact)
}

function initializeSheet (p, s, v) {
  const result = {
    type: 'expADI',
    surv_id: p.sv
  }

  initializeSurveillance(result, s)

  result.sheet_id = p.id
  result.type = p.f.expManagment
  result.v = p.v
  result.st = p.st
  result.unit_type = unitType(s.year, p.f.unitType)
  result.uf = p.f.uf
  result.sheet_number = p.f.sheetNumber
  result.birthdate = newDate(p.f.birthdate)
  if (result.type != null && result.type >= 1 && result.type <= 3) {
    const ifs = ['bacteremia', 'pneumopathy', 'sepsis']
    const i = ifs[result.type - 1]
    result.age_years = episodAge(p.f, p.f[i])
    result.age_days = episodDays(p.f, p.f[i])
  } else {
    result.age_years = null
    result.age_days = null
  }
  result.sex = p.f.sex
  result.entry = newDate(p.f.entry)

  result.immuno = p.f.immuno
  result.cancer = p.f.cancer
  result.antibio = p.f.antibio
  const neo = p.f.birthWeight != null
  result.neo_origin = neo ? p.f.origin : null
  result.neo_birth_site = p.f.birthSite
  result.neo_birth_weight = p.f.birthWeight
  result.neo_gestational_age = p.f.gestationalAge
  result.neo_ventilation = p.f.ventilation
  result.neo_leucomalacy = p.f.leucomalacy
  result.cribII = score('cribII', p.f.mainSeverity)
  result.ad_ped_origin = neo ? null : p.f.origin
  if (v >= 3) {
    result.ad_ped_bmi = p.f.bmi
  }
  result.ad_ped_rea_trauma = p.f.trauma
  result.ad_ped_rea_diagnosis = p.f.diagnosis
  result.igsII = score('igsII', p.f.mainSeverity)
  result.pimII = score('pimII', p.f.mainSeverity)
  result.other_score_name = p.f.otherSeverity.code
  result.other_score_value = p.f.otherSeverity.value
  result.dead = p.f.dead
  result.death = newDate(p.f.death)
  result.pneu_ecmo = p.f.ecmo
  result.pneu_intubated = p.f.intubated
  result.pneu_intub_start = newDate(p.f.intubStart)
  result.pneu_reintubated = p.f.reintubated
  result.pneu_intub_end = newDate(p.f.intubEnd)
  result.portage_bmr_bhre = p.f.portageBmrBhre

  return result
}

const reducer = (v) => (result, p) => {
  const s = findSurveillance(result, p)
  if (s != null && result.p.onlyClosed && s.status !== 'closed') {
    --result.r.length
    return result
  }

  const sheet = initializeSheet(p, s, v)
  if (result.keys == null) {
    result.keys = Object.keys(sheet)
  }

  p.f.bmrBhreMicroOrgs.forEach(
    (c, i) => addBMRBHreMO(
      `portage_bmr_bhre_mo${strIndex(i + 1)}`, sheet, p.f, c
    )
  )

  if (sheet.type === 1) {
    const bv = [2, 3, 4, 5, 5, 6]
    addBacteremia(`bact01`, sheet, p.f, p.f.bacteremia, bv[v - 1])
  } else if (sheet.type === 2) {
    const pv = [2, 2, 3, 3, 3, 3]
    addPneumopathy(`pneu01`, sheet, p.f, p.f.pneumopathy, pv[v - 1], true)
  } else {
    addSepsis(`sep01`, sheet, p.f, p.f.sepsis, v)
  }

  extractKeys(result, sheet, ['portage_bmr_bhre_mo', 'bact', 'pneu', 'sep'])

  result.r[result.i++] = sheet
  return result
}

function run (v) {
  return (params) => {
    return convert(
      params, reducer(v), ['portage_bmr_bhre_mo', 'bact', 'pneu', 'sep']
    )
  }
}

export default {
  '2020': run(1),
  '2021': run(2),
  '2022': run(2),
  '2023': run(3),
  '2024': run(4),
  '2025': run(6),
  '2026': run(6)
}
